import { type RefSelectProps } from 'antd'
import { useAtomValue } from 'jotai'
import { type Ref, forwardRef, useCallback, useMemo, useState } from 'react'
import { FaSyncAlt } from 'react-icons/fa'

import { Select } from '@/components/elements'
import { type SingleOrMultipleSelectProps } from '@/components/types'
import {
  DESCRIPTIONS,
  ERROR_MESSAGES
} from '@/constants/common/message.constants'
import { type PartnerSelectEntity } from '@/libs/api'
import { useSelectOnChangeHandler } from '@/libs/hooks'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'

import { usePartnersSearchQuery } from '../../hooks'
import { mapToPartnerFieldName } from '../PartnerSelect'

type Props = SingleOrMultipleSelectProps<number, PartnerSelectEntity> & {
  tenantKey: string
  /** 拠点ID指定（指定しない場合は選択された拠点IDを使用して絞り込まれる） */
  dcIds?: number[]
  /** 検索・並び順に拠点名を含めるか */
  shouldIncludeDcNameForSearch?: boolean
  /** 候補ラベルの先頭に拠点名を表示するか（未指定時は shouldIncludeDcNameForSearch に従う） */
  shouldIncludeDcNameInLabel?: boolean
  debounceDelay?: number
  partnerIds?: number[]
}

/**
 * パートナーをインクリメンタルサーチで選択するSelect
 */
export const PartnerSearchSelect = forwardRef(
  (
    {
      className,
      placeholder = DESCRIPTIONS.SEARCHING_PARTNERS,
      mode,
      onChange,
      tenantKey,
      dcIds,
      shouldIncludeDcNameForSearch,
      shouldIncludeDcNameInLabel = shouldIncludeDcNameForSearch,
      value,
      debounceDelay = 300,
      partnerIds,
      ...props
    }: Props,
    ref: Ref<RefSelectProps> | undefined
  ) => {
    const [searchValue, setSearchValue] = useState('')
    const selectedDcId = useAtomValue(selectedDcIdAtom)
    const { data, isFetching, isLoading } = usePartnersSearchQuery(
      tenantKey,
      {
        name: searchValue,
        dcIds: dcIds ?? [selectedDcId],
        shouldIncludeDcNameForSearch,
        incrementalSearchPartnerIds: value
          ? Array.isArray(value)
            ? value
            : [value]
          : undefined,
        orders: shouldIncludeDcNameForSearch
          ? ['dcName.asc', 'dcId.asc', 'code.asc']
          : ['code.asc'],
        partnerIds
      },
      {
        debounceDelay
      }
    )

    const options = useMemo(() => {
      return data?.map((partner) => ({
        ...partner,
        // nameで指定すると利用側で取得できるoption.nameの値が変わるため、labelで指定
        label: mapToPartnerFieldName(partner, shouldIncludeDcNameInLabel)
      }))
    }, [data, shouldIncludeDcNameInLabel])

    const handleChange = useSelectOnChangeHandler({ mode, onChange })

    const handleSearch = useCallback((value: string) => {
      setSearchValue(value)
    }, [])

    const handleClear = useCallback(() => {
      setSearchValue('')
    }, [])

    return (
      <Select
        customRef={ref}
        mode={mode}
        value={isLoading ? null : value}
        placeholder={placeholder}
        className={className}
        loading={isFetching}
        showSearch
        filterOption={false}
        searchValue={searchValue}
        onSearch={handleSearch}
        onClear={handleClear}
        onChange={handleChange}
        notFoundContent={
          isFetching ? (
            <div className="flex items-center justify-center p-2">
              <FaSyncAlt className="mr-2 animate-spin" />
              <span>{DESCRIPTIONS.SEARCHING}</span>
            </div>
          ) : searchValue && !data?.length ? (
            <div className="p-2 text-center text-gray-500">
              {ERROR_MESSAGES.PARTNER_NOT_FOUND}
            </div>
          ) : null
        }
        allowClear
        options={options}
        popupMatchSelectWidth={!shouldIncludeDcNameForSearch}
        {...props}
      ></Select>
    )
  }
)

PartnerSearchSelect.displayName = 'PartnerSearchSelect'
